import { useState, useMemo, useEffect } from 'react'

import { Typography, Box } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faLock } from '@fortawesome/pro-solid-svg-icons'
import { noop } from 'lodash'

import { useAppRoundEditDisabled } from 'store/feature-toggle/hooks'
import { useApplicationIntegrationEnabled } from 'store/college/hooks'
import api from 'common/api'
import SupportTicketLink from 'components/SupportTicketLink'
import useCollegeName from 'common/use-college-name'
import useIsShadow from 'common/use-is-shadow'
import { useScid, useFirstName, useLastName, useEmail, useCanManageRoundsAndRequirements } from 'store/user/hooks'
import { useFocusedApplicationCycle } from './AppRoundProvider'

const modalTitle = 'Request Submitted'
const modalBody =
  'Your request to edit an application round was submitted to support. Our team will contact you shortly.'

type ConfigurationAPI = {
  verification?: { activated: boolean }
  scoirApplyEnabled: boolean
}

const AppCycleRounds = ({ isCurrent }: { isCurrent: boolean }) => {
  const [appRoundsActive, setAppRoundsActive] = useState(false)
  const applicationCycle = useFocusedApplicationCycle()
  const appRoundsFeatureDisabled = useAppRoundEditDisabled()
  const canManageRounds = useCanManageRoundsAndRequirements()
  const isAppIntegrationEnabled = useApplicationIntegrationEnabled()

  const collegeName = useCollegeName()
  const scid = useScid()
  const firstName = useFirstName()
  const lastName = useLastName()
  const email = useEmail()
  const isShadow = useIsShadow()

  useEffect(() => {
    if (isAppIntegrationEnabled && !appRoundsFeatureDisabled) {
      api
        .get<ConfigurationAPI>(`/api/colleges/${scid}/integrations/application/configuration/${applicationCycle}`)
        .then((config) => {
          setAppRoundsActive((config.verification?.activated ?? false) && config.scoirApplyEnabled)
        })
        .catch(noop)
    }
  }, [scid, appRoundsFeatureDisabled, applicationCycle, isAppIntegrationEnabled])

  const supportTicketConfig = useMemo(
    () => ({
      type: 'CollegeApplicationRoundChangeRequest',
      firstName,
      lastName,
      email,
      subjectContext: collegeName,
      scid,
    }),
    [collegeName, scid, firstName, lastName, email],
  )
  if (
    isShadow ||
    (!isCurrent && canManageRounds) ||
    (!appRoundsFeatureDisabled && canManageRounds && !appRoundsActive)
  ) {
    return null
  }
  return (
    <Box mb={3}>
      {appRoundsFeatureDisabled ? (
        <Typography
          variant="body1"
          align="center"
        >
          Your school&apos;s application rounds cannot be edited at this time.&nbsp;
          <SupportTicketLink
            ticketConfig={supportTicketConfig}
            modalTitle={modalTitle}
            modalBody={modalBody}
          >
            Contact support
          </SupportTicketLink>
          &nbsp;to request changes.
        </Typography>
      ) : !canManageRounds ? (
        <Alert severity="info">
          <strong>Read-Only View:</strong> You currently don&apos;t have the permissions to update this page. If you
          would like access, please contact the Account Administrator at your institution and request the Application
          Manager role.
        </Alert>
      ) : (
        <Alert
          color="info"
          severity="info"
          data-testid="alert"
          icon={<FontAwesomeIcon icon={faLock as IconDefinition} />}
        >
          Once the application is activated and locked, the ability to remove certain rounds is no longer available.
        </Alert>
      )}
    </Box>
  )
}

export default AppCycleRounds
